import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter } from "react-router-dom";
import {
  ArrowUpRight, Banknote, BarChart3, Bell, Box, CalendarClock, ChevronRight, LayoutDashboard,
  LogOut, Menu, Plus, Receipt, Search, Settings2, UserRound, Users, WalletCards, X, UsersRound,
  Activity, FileBarChart2, AlertTriangle, Wrench, ClipboardCheck, MonitorDown, Zap, ShieldCheck,
  ClipboardList, Download, Briefcase, Calculator, Car, Home, CalendarDays, Satellite, Tags, HandCoins,
} from "lucide-react";
import "@/App.css";

import { api, API, canView, can, goTo } from "./lib/api";
import { Modal, useMobileTableLabels } from "./lib/ui";
import Dashboard from "./pages/Dashboard";
import DetailPage from "./pages/Detail";
import WorkPanel, { WorkOrderForm } from "./pages/WorkPanel";
import WorkOrders from "./pages/WorkOrders";
import RuhsatArchive from "./pages/RuhsatArchive";
import ServiceTickets from "./pages/Service";
import Warranty from "./pages/Warranty";
import Appointments from "./pages/Appointments";
import MobileHome from "./pages/MobileHome";
import Purchases from "./pages/Purchases";
import Records from "./pages/Records";
import Customers from "./pages/Customers";
import Stocks from "./pages/Stocks";
import Services from "./pages/Services";
import Notes from "./pages/Notes";
import Invoices from "./pages/Invoices";
import Banks from "./pages/Banks";
import Cheques from "./pages/Cheques";
import Payments from "./pages/Payments";
import UsersPage from "./pages/Users";
import ActivityLog from "./pages/Activity";
import TaxReport from "./pages/TaxReport";
import Installs from "./pages/Installs";
import PriceList from "./pages/PriceList";
import Payroll from "./pages/Payroll";
import { BankImportModal } from "./pages/BankImport";
import {
  RecordModal, StockModal, CustomerModal, CustomerTransferModal, InvoiceModal, InvoiceViewModal,
  AccountModal, TransactionModal, ChequeModal, UserModal, UserPermsModal,
  TransferModal, StatementModal, BulkStatusModal, SettingsModal, QuickPaymentModal,
  StockMovementsModal, ServiceModal, UserActivityModal, NoteModal, StockPriceHistoryModal,
  TransactionDetailModal, ChequeDetailModal,
} from "./pages/Modals";

const NAV = [
  { id: "dashboard", label: "Genel Bakış", icon: LayoutDashboard },
  { id: "records", label: "Gelir · Gider", icon: BarChart3 },
  { id: "invoices", label: "Faturalar", icon: Receipt },
  { id: "purchases", label: "Alım Faturaları", icon: Download },
  { id: "work-orders", label: "İş Fişleri", icon: ClipboardList },
  { id: "ruhsat-archive", label: "Ruhsat Arşivi", icon: Car },
  { id: "installs", label: "UTTS ve Takip", icon: Satellite, module: "utts" },
  { id: "payroll", label: "Maaş & Personel", icon: HandCoins, module: "payroll" },
  { id: "service-tickets", label: "Servis", icon: Wrench, module: "service" },
  { id: "warranty", label: "Garanti", icon: ShieldCheck, module: "warranty" },
  { id: "appointments", label: "Randevular", icon: CalendarDays },
  { id: "customers", label: "Cari Hesaplar", icon: Users },
  { id: "banks", label: "Banka & Kasa", icon: Banknote, module: "banks" },
  { id: "payments", label: "Ödemeler", icon: CalendarClock },
  { id: "cheques", label: "Çekler", icon: WalletCards, module: "cheques" },
  { id: "stocks", label: "Stoklar", icon: Box },
  { id: "services", label: "Hizmetler", icon: Wrench },
  { id: "price-list", label: "Fiyat Listesi", icon: Tags, module: "price_list" },
  { id: "notes", label: "Notlar", icon: ClipboardCheck },
  { id: "tax", label: "Vergi Raporu", icon: FileBarChart2 },
  { id: "users", label: "Kullanıcılar", icon: UsersRound, module: "users" },
  { id: "activity", label: "Aktivite Kaydı", icon: Activity, module: "users" },
];

const IS_PREVIEW = window.location.hostname.includes("preview.emergentagent.com");
if (IS_PREVIEW) {
  const last = Number(localStorage.getItem("sp_synced_at") || 0);
  if (Date.now() - last > 2 * 3600 * 1000) localStorage.removeItem("fp_token");
  else localStorage.setItem("sp_synced_at", String(Date.now()));
}

function Login({ onLogin }) {
  const [panel, setPanel] = useState(null); // "accounting" | "work"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [syncMsg, setSyncMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (IS_PREVIEW) {
        setSyncMsg("Yayındaki veriler önizlemeye alınıyor…");
        const { data: s } = await api.post("/admin/sync-from-live", { email, password }, { timeout: 120000 }).catch((er) => {
          if (er.response?.status === 401) throw er;
          return { data: { ok: false, reason: "Yayına ulaşılamadı" } };
        });
        setSyncMsg(s.ok ? `Yayından ${s.total} kayıt alındı.` : `Yayın verisi alınamadı: ${s.reason || "atlandı"}`);
        localStorage.setItem("sp_synced_at", String(Date.now()));
      }
      const { data } = await api.post("/auth/login", { email, password, panel });
      localStorage.setItem("fp_token", data.token);
      localStorage.setItem("sp_panel", data.panel || panel);
      // Hesaba kayıtlı görünüm tercihlerini uygula
      const p = data.user?.preferences;
      if (p && (p.theme || p.font_size || p.font_weight)) {
        document.documentElement.setAttribute("data-theme", p.theme || "light");
        document.documentElement.setAttribute("data-font-size", p.font_size || "normal");
        document.documentElement.setAttribute("data-font-weight", p.font_weight || "normal");
        localStorage.setItem("sp_theme", p.theme || "light");
        localStorage.setItem("sp_font_size", p.font_size || "normal");
        localStorage.setItem("sp_font_weight", p.font_weight || "normal");
      }
      onLogin(data.user);
    } catch (err) {
      setSyncMsg("");
      const d = err.response?.data?.detail;
      if (typeof d === "string") setError(d);
      else if (!err.response) setError(`Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip sayfayı yenileyin. (${err.message || "ağ hatası"})`);
      else setError(`Giriş yapılamadı (hata ${err.response.status})`);
    }
  };
  if (!panel) {
    return (
      <main className="login-page">
        <div className="login-hero">
          <div className="login-hero-inner">
            <img src="/logo-serhan.png" alt="Serhan Elektronik" className="login-hero-logo" />
            <div className="login-hero-caption">ELEKTRONİK MUHASEBE</div>
            <h2>Hoş geldiniz. <em>Hangi panele girmek istersiniz?</em></h2>
            <p>Muhasebe tüm finansal operasyonları yönetir. İş Takip sadece saha personelinin iş fişi oluşturması içindir.</p>
          </div>
        </div>
        <div className="login-form-side">
          <div className="panel-picker" data-testid="panel-picker">
            <button type="button" data-testid="pick-accounting-panel" className="panel-pick-card"
                    onClick={() => setPanel("accounting")}>
              <div className="pick-icon acc"><Calculator size={28} /></div>
              <h3>Muhasebe</h3>
              <small>Fatura · cari · stok · banka · raporlar</small>
              <span className="pick-cta">Giriş yap <ArrowUpRight size={14} /></span>
            </button>
            <button type="button" data-testid="pick-work-panel" className="panel-pick-card"
                    onClick={() => setPanel("work")}>
              <div className="pick-icon work"><Briefcase size={28} /></div>
              <h3>İş Takip</h3>
              <small>Personel iş fişi (müşteri, plaka, ruhsat, ürün/hizmet)</small>
              <span className="pick-cta">Giriş yap <ArrowUpRight size={14} /></span>
            </button>
          </div>
        </div>
      </main>
    );
  }
  return (
    <main className="login-page">
      <div className="login-hero">
        <div className="login-hero-inner">
          <img src="/logo-serhan.png" alt="Serhan Elektronik" className="login-hero-logo" />
          <div className="login-hero-caption">ELEKTRONİK MUHASEBE</div>
          <h2>{panel === "work" ? <>Saha personeli için <em>İş Takip.</em></> : <>İşletmenizin finansı, <em>tek ekranda.</em></>}</h2>
          <p>{panel === "work" ? "İş fişi doldurun, muhasebeye otomatik düşsün. Fatura kesimi muhasebenin kontrolünde kalır." : "Fatura, cari, stok, çek ve kasa yönetimi."}</p>
          <ul className="login-hero-points">
            <li><Zap size={14} /> {panel === "work" ? "Müşteri, plaka, ruhsat, kalemler" : "Fatura & proforma, otomatik KDV"}</li>
            <li><Zap size={14} /> {panel === "work" ? "Muhasebeye beklemede bildirim" : "Cari hesap, çek ve vade takibi"}</li>
            <li><Zap size={14} /> {panel === "work" ? "Hızlı mobil form, offline çalışır" : "Stok, hizmet ve vergi raporları"}</li>
          </ul>
        </div>
      </div>
      <div className="login-form-side">
        <section className="login-card">
          <div className="eyebrow"><ShieldCheck size={13} /> {panel === "work" ? "İŞ TAKİP GİRİŞİ" : "MUHASEBE GİRİŞİ"}</div>
          <h1>İşinizi net görün.</h1>
          <p>{panel === "work" ? "İş fişi oluşturma ekranına girmek için bilgilerinizi girin." : "Finansal operasyonlarınızı tek yerden yönetin."}</p>
          <form onSubmit={submit}>
            <label>E-posta<input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required /></label>
            <label>Şifre<input data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required /></label>
            {IS_PREVIEW && !syncMsg && <div data-testid="preview-sync-info" className="form-error" style={{ background: "#eef6ff", color: "#1e4d8c" }}>Önizleme: giriş yaparken yayındaki (hanelektronet.com) tüm veriler buraya kopyalanır; önizlemedeki deneme kayıtları silinir.</div>}
            {syncMsg && <div data-testid="preview-sync-status" className="form-error" style={{ background: "#eef6ff", color: "#1e4d8c" }}>{syncMsg}</div>}
            {error && <div data-testid="login-error" className="form-error">{error}</div>}
            <button data-testid="login-submit-button" className="primary-button" type="submit">
              Panele giriş yap <ArrowUpRight size={17} />
            </button>
            <button type="button" className="secondary-button" style={{ marginTop: 8, width: "100%", justifyContent: "center" }} onClick={() => { setPanel(null); setError(""); }}>
              ← Panel seçimine dön
            </button>
          </form>
          <small>Serhan Elektronik Muhasebe</small>
        </section>
      </div>
    </main>
  );
}

function InstallButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => {});
      setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };

  return (
    <>
      <button data-testid="pwa-install-button" type="button" className="bell-btn"
              onClick={onClick} title="Masaüstüne yükle — tarayıcısız, kendi penceresinde çalışır">
        <MonitorDown size={17} />
      </button>
      {showHelp && (
        <Modal title="Masaüstüne nasıl yüklerim?" onClose={() => setShowHelp(false)} testid="pwa-help-modal">
          <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.7, padding: "4px 2px" }}>
            <p><strong>Chrome / Edge:</strong> Adres çubuğunun sağındaki <em>⊕ yükle simgesine</em> tıklayın veya tarayıcı menüsünden <em>“Serhan Elektronik Muhasebe uygulamasını yükle”</em> seçeneğini seçin.</p>
            <p><strong>Mobil (Android):</strong> Tarayıcı menüsünden <em>“Ana ekrana ekle”</em> seçeneğini seçin.</p>
            <p><strong>iPhone / iPad:</strong> Paylaş butonu → <em>“Ana Ekrana Ekle”</em>.</p>
            <p style={{ marginTop: 8, color: "#64748b" }}>
              Yükleme sonrası Serhan Elektronik Muhasebe kendi penceresinde, masaüstü simgesiyle açılır ve otomatik güncellenir.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}

function AppointmentBell({ dash, setPage }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const today_items = dash?.appointments_today || [];
  const tomorrow_count = dash?.appointments_tomorrow_count || 0;
  const total = today_items.length + tomorrow_count;
  const go = () => { setPage("appointments"); setOpen(false); };
  return (
    <div className="bell-wrap" ref={ref}>
      <button data-testid="appointment-bell-button" type="button" className="bell-btn" onClick={() => setOpen((o) => !o)}
              title="Bugünkü randevular">
        <CalendarDays size={17} />
        {total > 0 && <span className="bell-badge" data-testid="appointment-bell-count">{total}</span>}
      </button>
      {open && (
        <div className="bell-dropdown" data-testid="appointment-bell-panel">
          <div className="bell-head">
            <strong>Bugünün Randevuları</strong>
            <small>{today_items.length} bugün · {tomorrow_count} yarın</small>
          </div>
          {total === 0 && <div className="bell-empty">Bugün ve yarın randevu yok.</div>}
          {today_items.length > 0 && (
            <div className="bell-section">
              <div className="bell-section-head"><CalendarClock size={12} /> Bugün ({today_items.length})</div>
              {today_items.slice(0, 8).map((a) => (
                <button key={a.id} type="button" className="bell-item"
                        data-testid={`bell-appointment-${a.id}`} onClick={go}>
                  <div>
                    <strong>{a.title || a.customer_name || "Randevu"}</strong>
                    <small>{a.customer_name || "—"}{a.plate ? ` · ${a.plate}` : ""}</small>
                  </div>
                  <span className="bell-amount">{a.time || "--:--"}</span>
                </button>
              ))}
            </div>
          )}
          {tomorrow_count > 0 && (
            <div className="bell-section">
              <div className="bell-section-head warn">
                <CalendarClock size={12} /> Yarın da {tomorrow_count} randevu var
              </div>
            </div>
          )}
          <button type="button" className="bell-footer" onClick={go} data-testid="bell-goto-appointments">
            Randevu takvimine git <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function WorkOrderChangeBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unseen: 0, items: [] });
  const ref = useRef(null);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => { clearInterval(t); document.removeEventListener("mousedown", handler); };
  }, []);
  const seen = async (id) => { await api.post(`/notifications/${id}/seen`).catch(() => {}); load(); };
  const openWo = (n) => { if (!n.seen) seen(n.id); goTo(`/is-fisi/${n.work_order_id}`); };
  return (
    <div className="bell-wrap" ref={ref}>
      <button data-testid="wo-change-bell-button" type="button" className="bell-btn" onClick={() => setOpen((o) => !o)}
              title="İşlenmiş iş fişlerinde yapılan değişiklikler">
        <ClipboardList size={17} />
        {data.unseen > 0 && <span className="bell-badge" data-testid="wo-change-bell-count">{data.unseen}</span>}
      </button>
      {open && (
        <div className="bell-dropdown" data-testid="wo-change-bell-panel">
          <div className="bell-head">
            <strong>İş Fişi Bildirimleri</strong>
            <small>Değişiklikler ve iptal talepleri · {data.unseen} yeni</small>
          </div>
          {data.items.length === 0 && <div className="bell-empty">Bildirim yok.</div>}
          {data.items.slice(0, 15).map((n) => (
            <button key={n.id} type="button" className={`bell-item ${n.seen ? "seen" : "unseen"}`}
                    data-testid={`wo-change-${n.id}`} onClick={() => openWo(n)}>
              <div>
                <strong>{n.plate || "—"} · {n.customer_name || "—"}</strong>
                <small>{n.changed_by_name} · {new Date(n.created_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</small>
                {n.type === "work_order_cancel_request"
                  ? <small className="change-line" style={{ color: "#dc2626" }}><b>İptal talebi:</b> {n.changes?.[0]?.new}</small>
                  : (n.changes || []).slice(0, 3).map((c) => (
                  <small key={c.field} className="change-line"><b>{c.field}:</b> {c.old || "—"} → {c.new || "—"}</small>
                ))}
              </div>
              {!n.seen && <span className="bell-new-dot" />}
            </button>
          ))}
          {data.unseen > 0 && (
            <button type="button" className="bell-footer" onClick={() => seen("all")} data-testid="wo-change-seen-all">
              Tümünü görüldü işaretle <ChevronRight size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChequeBell({ dash, setPage }) {  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const overdue = dash?.alerts?.overdue_cheques || [];
  const upcoming = dash?.alerts?.upcoming_cheques || [];
  const total = overdue.length + upcoming.length;
  const horizon = dash?.alerts?.due_horizon_days ?? 7;
  const goCheques = () => { setPage("cheques"); setOpen(false); };
  return (
    <div className="bell-wrap" ref={ref}>
      <button data-testid="cheque-bell-button" type="button" className="bell-btn" onClick={() => setOpen((o) => !o)}
              title="Vadesi yaklaşan çekler">
        <Bell size={17} />
        {total > 0 && <span className="bell-badge" data-testid="cheque-bell-count">{total}</span>}
      </button>
      {open && (
        <div className="bell-dropdown" data-testid="cheque-bell-panel">
          <div className="bell-head">
            <strong>Vadesi Yaklaşan Çekler</strong>
            <small>{horizon} gün içindeki + vadesi geçmiş</small>
          </div>
          {total === 0 && (
            <div className="bell-empty">Bekleyen çek uyarısı yok.</div>
          )}
          {overdue.length > 0 && (
            <div className="bell-section">
              <div className="bell-section-head danger">
                <AlertTriangle size={12} /> Vadesi Geçmiş ({overdue.length})
              </div>
              {overdue.slice(0, 6).map((c) => (
                <button key={c.id} type="button" className="bell-item"
                        data-testid={`bell-cheque-${c.id}`} onClick={goCheques}>
                  <div>
                    <strong>{c.customer_name || c.check_no || "Çek"}</strong>
                    <small>{c.check_no || "-"} · Vade: {c.maturity_date}</small>
                  </div>
                  <span className="bell-amount negative">
                    {c.direction === "received" ? "+" : "-"}
                    {Number(c.amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                  </span>
                </button>
              ))}
            </div>
          )}
          {upcoming.length > 0 && (
            <div className="bell-section">
              <div className="bell-section-head warn">
                <CalendarClock size={12} /> Yaklaşan ({upcoming.length})
              </div>
              {upcoming.slice(0, 6).map((c) => (
                <button key={c.id} type="button" className="bell-item"
                        data-testid={`bell-cheque-${c.id}`} onClick={goCheques}>
                  <div>
                    <strong>{c.customer_name || c.check_no || "Çek"}</strong>
                    <small>{c.check_no || "-"} · Vade: {c.maturity_date}</small>
                  </div>
                  <span className="bell-amount">
                    {c.direction === "received" ? "+" : "-"}
                    {Number(c.amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                  </span>
                </button>
              ))}
            </div>
          )}
          {total > 0 && (
            <button type="button" className="bell-footer" onClick={goCheques} data-testid="bell-goto-cheques">
              Tüm çekleri görüntüle →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Shell({ user, onLogout, onUserChange }) {
  const [page, setPage] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("page");
    const valid = ["dashboard", "records", "invoices", "purchases", "work-orders", "customers", "banks", "payments", "cheques",
                   "stocks", "services", "notes", "tax", "users", "activity", "settings", "ruhsat-archive", "m-home",
                   "service-tickets", "warranty", "appointments"];
    if (valid.includes(p)) return p;
    return window.matchMedia("(max-width: 900px)").matches ? "m-home" : "dashboard";
  });
  const [openSide, setOpenSide] = useState(false);
  useEffect(() => {
    if (page !== "banks") { sessionStorage.removeItem("banks_checked"); sessionStorage.removeItem("banks_selected"); }
    const url = new URL(window.location.href);
    if (url.searchParams.get("page") !== page) {
      url.searchParams.set("page", page);
      window.history.replaceState(window.history.state, "", url);
    }
  }, [page]);
  const [modal, setModal] = useState(() => {
    const cid = new URLSearchParams(window.location.search).get("statement");
    return cid ? { type: "statement", customerId: cid } : null;
  });
  useEffect(() => {
    const h = (e) => { const d = e.detail; if (d?.id) setCustomers((list) => list.map((c) => (c.id === d.id ? { ...c, ...d } : c))); };
    window.addEventListener("sp-customer-updated", h);
    return () => window.removeEventListener("sp-customer-updated", h);
  }, []);
  const [dash, setDash] = useState({ income: 0, expense: 0, balance: 0, upcoming: 0, recent: [], bank_balance: 0, invoice_count: 0, customer_count: 0 });
  const [records, setRecords] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [services, setServices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notesPending, setNotesPending] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [users, setUsers] = useState([]);
  const [availablePerms, setAvailablePerms] = useState([]);
  const [invoiceScope, setInvoiceScope] = useState("active");

  const showBanks = canView(user, "banks");
  const showCheques = canView(user, "cheques");
  const showUsers = canView(user, "users");
  const visibleNav = NAV.filter((n) => !n.module || canView(user, n.module));

  const load = async () => {
    const calls = [
      api.get("/dashboard").then((r) => setDash(r.data)),
      api.get("/records").then((r) => setRecords(r.data)),
      api.get("/stocks").then((r) => setStocks(r.data)),
      api.get("/services").then((r) => setServices(r.data)),
      api.get("/notes").then((r) => setNotes(r.data)),
      api.get("/notes/pending-count").then((r) => setNotesPending(r.data.count)),
      api.get(`/invoices?scope=${invoiceScope}`).then((r) => setInvoices(r.data)),
      api.get("/customers").then((r) => setCustomers(r.data)),
      api.get("/auth/permissions").then((r) => setAvailablePerms(r.data.available)),
    ];
    if (showBanks) {
      calls.push(api.get("/accounts").then((r) => setAccounts(r.data)));
      calls.push(api.get("/transactions").then((r) => setTransactions(r.data)));
    }
    if (showCheques) {
      calls.push(api.get("/cheques").then((r) => setCheques(r.data)));
    }
    if (showUsers) {
      calls.push(api.get("/users").then((r) => setUsers(r.data)));
    }
    await Promise.all(calls);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceScope]);

  // Beklemede iş fişi sayısı için canlı polling (30 sn)
  useEffect(() => {
    const tick = async () => {
      try {
        const { data } = await api.get("/dashboard");
        setDash((d) => ({ ...d, pending_work_orders: data.pending_work_orders ?? d.pending_work_orders, upcoming: data.upcoming ?? d.upcoming }));
      } catch { /* ignore */ }
    };
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  // İş fişinden fatura köprüsü: /?page=invoices&prefill=1 ile açıldıysa modal'ı prefill ile aç
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("prefill") === "1" && page === "invoices") {
      try {
        const raw = localStorage.getItem("sp_prefill_invoice");
        if (raw) {
          localStorage.removeItem("sp_prefill_invoice");
          const prefill = JSON.parse(raw);
          setModal({ type: "invoice", prefill });
        }
      } catch { /* ignore */ }
      // temizle
      const url = new URL(window.location.href);
      url.searchParams.delete("prefill");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [page]);

  const active = page === "m-home" ? { label: "Ana Sayfa" } : (visibleNav.find((n) => n.id === page) || visibleNav[0]);

  // action handlers
  const deleteAndReload = async (path, ok = "Silindi") => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(path);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Silinemedi");
    }
  };

  const QUICK_ADD_PERM = {
    invoices: "create_invoice", stocks: "create_stock", customers: "create_customer",
    banks: "create_transaction", cheques: "edit_cheque", records: "create_record",
    services: "manage_users",
  };
  void QUICK_ADD_PERM;

  return (
    <div className="app-shell">
      <aside className={openSide ? "sidebar mobile-open" : "sidebar"}>
        <div className="side-top">
          <img src="/logo-serhan.png" alt="Serhan Elektronik" className="brand-logo" />
          <span className="brand-caption">ELEKTRONİK MUHASEBE</span>
          <button data-testid="close-sidebar-button" className="icon-button mobile-only" onClick={() => setOpenSide(false)}>
            <X size={18} />
          </button>
        </div>
        <div className="workspace">
          <div className="workspace-avatar">SE</div>
          <div>
            <strong>Serhan Elektronik</strong>
            <small>Muhasebe çalışma alanı</small>
          </div>
          <ChevronRight size={15} />
        </div>
        <nav>
          {visibleNav.map(({ id, label, icon: Icon }) => (
            <button
              data-testid={`nav-${id}-button`}
              className={page === id ? "nav-item active" : "nav-item"}
              key={id}
              onClick={() => { setPage(id); setOpenSide(false); }}
            >
              <Icon size={18} />
              {label}
              {id === "notes" && notesPending > 0 ? <b data-testid="notes-pending-badge">{notesPending}</b> : null}
              {id === "work-orders" && dash.pending_work_orders > 0 ? <b className="badge-pulse" data-testid="work-orders-pending-badge">{dash.pending_work_orders}</b> : null}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {user.role === "admin" && (
            <button data-testid="open-settings-button" className="nav-item" onClick={() => setModal({ type: "settings" })}>
              <Settings2 size={18} />Ayarlar
            </button>
          )}
          <button data-testid="logout-button" className="nav-item" onClick={onLogout}>
            <LogOut size={18} />Çıkış yap
          </button>
          <div className="profile">
            <div className="profile-avatar">{user.name?.slice(0, 2).toUpperCase()}</div>
            <div>
              <strong>{user.name}</strong>
              <small>{user.role === "admin" ? "Yönetici" : "Yetkili kullanıcı"}</small>
            </div>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button data-testid="open-sidebar-button" className="icon-button mobile-only" onClick={() => setOpenSide(true)}>
            <Menu size={20} />
          </button>
          <div className="breadcrumbs">Serhan Elektronik <ChevronRight size={14} /> <strong>{active?.label}</strong></div>
          <div className="top-actions">
            <InstallButton />
            <AppointmentBell dash={dash} setPage={setPage} />
            <WorkOrderChangeBell />
            {(user.role === "admin" || user.permissions?.view_cheques !== false) && (
              <ChequeBell dash={dash} setPage={setPage} />
            )}
          </div>
        </header>

        <div className="content">
          {page === "m-home" && <MobileHome user={user} dash={dash} customers={customers} setPage={setPage} setModal={setModal} />}
          {page === "dashboard" && <Dashboard data={dash} setPage={setPage} setModal={setModal} user={user} />}
          {page === "records" && <Records rows={records} setModal={setModal} user={user} onDelete={(r) => deleteAndReload(`/records/${r.id}`)} />}
          {page === "invoices" && (
            <Invoices rows={invoices} setModal={setModal} user={user}
                      onDelete={(r) => deleteAndReload(`/invoices/${r.id}`)}
                      onView={(inv) => setModal({ type: "invoice-view", invoice: inv })}
                      onReload={load}
                      scope={invoiceScope} setScope={setInvoiceScope} />
          )}
          {page === "customers" && <Customers rows={customers} setModal={setModal} user={user} onReload={load}
                                              onDelete={(c) => deleteAndReload(`/customers/${c.id}`)}
                                              onOpenStatement={(c) => setModal({ type: "statement", customerId: c.id })}
                                              onQuickPayment={(c, direction) => setModal({ type: "quick-payment", customer: c, direction })} />}
          {page === "stocks" && <Stocks rows={stocks} setModal={setModal} user={user} onDelete={(s) => deleteAndReload(`/stocks/${s.id}`)} />}
          {page === "services" && <Services rows={services} setModal={setModal} user={user} onDelete={(s) => deleteAndReload(`/services/${s.id}`)} />}
          {page === "notes" && <Notes rows={notes} setModal={setModal} user={user} onReload={load} />}
          {page === "banks" && showBanks && (
            <Banks accounts={accounts} transactions={transactions} setModal={setModal} user={user} customers={customers}
                   onDeleteAccount={(a) => deleteAndReload(`/accounts/${a.id}`)}
                   onDeleteTx={(t) => deleteAndReload(`/transactions/${t.id}`)} />
          )}
          {page === "cheques" && showCheques && (
            <Cheques rows={cheques} setModal={setModal} user={user}
                     onDelete={(c) => deleteAndReload(`/cheques/${c.id}`)}
                     accounts={accounts}
                     onAction={async (c, action, extra = {}) => { await api.post(`/cheques/${c.id}/action`, { action, ...extra }); load(); }} />
          )}
          {page === "payments" && (
            <Payments records={records} invoices={invoices} cheques={cheques} canSeeCheques={showCheques} />
          )}
          {page === "users" && showUsers && (
            <UsersPage rows={users} setModal={setModal} me={user}
                       onDelete={(u) => deleteAndReload(`/users/${u.id}`)}
                       onEditPerms={(u) => setModal({ type: "user-perms", user: u })} />
          )}
          {page === "activity" && user.role === "admin" && <ActivityLog onReload={load} />}
          {page === "work-orders" && <WorkOrders user={user} />}
          {page === "ruhsat-archive" && <RuhsatArchive />}
          {page === "service-tickets" && <ServiceTickets user={user} customers={customers}
                                                         onCustomerAdded={(c) => setCustomers([c, ...customers])} />}
          {page === "warranty" && <Warranty user={user} customers={customers} stocks={stocks}
                                            onCustomerAdded={(c) => setCustomers([c, ...customers])}
                                            reload={load} />}
          {page === "appointments" && <Appointments user={user} customers={customers}
                                                    onCustomerAdded={(c) => setCustomers([c, ...customers])} />}
          {page === "purchases" && <Purchases user={user} customers={customers} accounts={accounts}
                                              stocks={stocks} services={services} />}
          {page === "tax" && <TaxReport />}
          {page === "installs" && canView(user, "utts") && <Installs user={user} customers={customers} />}
          {page === "price-list" && canView(user, "price_list") && <PriceList user={user} stocks={stocks} />}
          {page === "payroll" && canView(user, "payroll") && <Payroll accounts={accounts} onAccountsChanged={load} />}
        </div>
      </main>

      <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
        <button data-testid="mnav-home" className={page === "m-home" ? "active" : ""} onClick={() => setPage("m-home")}>
          <Home size={20} /><span>Ana Sayfa</span>
        </button>
        <button data-testid="mnav-invoices" className={page === "invoices" ? "active" : ""} onClick={() => setPage("invoices")}>
          <Receipt size={20} /><span>Faturalar</span>
        </button>
        <button data-testid="mnav-customers" className={page === "customers" ? "active" : ""} onClick={() => setPage("customers")}>
          <Users size={20} /><span>Cari</span>
        </button>
        <button data-testid="mnav-work-orders" className={page === "work-orders" ? "active" : ""} onClick={() => setPage("work-orders")}>
          <ClipboardList size={20} /><span>Fişler</span>
          {dash.pending_work_orders > 0 && <i className="mnav-badge" data-testid="mnav-wo-badge">{dash.pending_work_orders}</i>}
        </button>
        <button data-testid="mnav-menu" onClick={() => setOpenSide(true)}>
          <Menu size={20} /><span>Menü</span>
        </button>
      </nav>

      {modal?.type === "record" && <RecordModal onClose={() => setModal(null)} onSaved={load} initial={modal.initial || {}} />}
      {modal?.type === "work-order" && (
        <Modal title="Yeni İş Fişi" subtitle="Fiş kaydedilince muhasebeye beklemede olarak düşer."
               onClose={() => setModal(null)} size="lg" testid="mobile-workorder-modal">
          <WorkOrderForm stocks={stocks} onSaved={() => { setModal(null); load(); }} />
        </Modal>
      )}
      {modal?.type === "stock" && <StockModal onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "service" && <ServiceModal onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "note" && <NoteModal onClose={() => setModal(null)} onSaved={load} users={users} />}
      {modal?.type === "price-history" && <StockPriceHistoryModal stockId={modal.stockId} onClose={() => setModal(null)} />}
      {modal?.type === "user-activity" && <UserActivityModal userId={modal.userId} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "customer" && <CustomerModal onClose={() => setModal(null)} onSaved={load} initial={modal.initial} />}
      {modal?.type === "customer-transfer" && <CustomerTransferModal onClose={() => setModal(null)} onSaved={load} customers={customers} fromCustomerId={modal.fromCustomerId} />}
      {modal?.type === "invoice" && (
        <InvoiceModal onClose={() => setModal(null)} onSaved={load}
                      customers={customers} stocks={stocks} accounts={accounts}
                      services={services}
                      prefill={modal.prefill}
                      onCustomerAdded={(c) => setCustomers([c, ...customers])} />
      )}
      {modal?.type === "invoice-view" && <InvoiceViewModal invoice={modal.invoice} onClose={() => setModal(null)} onConverted={load} />}
      {modal?.type === "account" && <AccountModal onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "transaction" && (
        <TransactionModal onClose={() => setModal(null)} onSaved={load}
                          accounts={accounts} customers={customers} defaultAccountId={modal.account_id} />
      )}
      {modal?.type === "bank-import" && (
        <BankImportModal onClose={() => setModal(null)} onSaved={load} accounts={accounts} customers={customers}
                         transactions={transactions} defaultAccountId={modal.account_id} />
      )}
      {modal?.type === "cheque" && <ChequeModal onClose={() => setModal(null)} onSaved={load} customers={customers} cheques={cheques} />}
      {modal?.type === "user" && <UserModal onClose={() => setModal(null)} onSaved={load} availablePerms={availablePerms} />}
      {modal?.type === "user-perms" && (
        <UserPermsModal user={modal.user} me={user} onClose={() => setModal(null)} onSaved={() => { load(); if (modal.user.id === user.id) api.get("/auth/me").then((r) => onUserChange(r.data)).catch(() => {}); }} availablePerms={availablePerms} />
      )}
      {modal?.type === "transfer" && (
        <TransferModal onClose={() => setModal(null)} onSaved={load} accounts={accounts} />
      )}
      {modal?.type === "statement" && (
        <StatementModal customerId={modal.customerId}
          onClose={() => {
            setModal(null);
            const url = new URL(window.location.href);
            if (url.searchParams.has("statement")) { url.searchParams.delete("statement"); window.history.replaceState(window.history.state, "", url); }
          }}
          onOpenEntry={(kind, obj, e) => {
            const path = kind === "invoice" ? `/detay/fatura/${obj.id}`
              : kind === "transaction" ? `/detay/hareket/${obj.id}`
              : `/detay/cek/${obj.id}`;
            const url = new URL(window.location.href);
            url.searchParams.set("statement", modal.customerId);
            window.history.replaceState(window.history.state, "", url);
            goTo(path, e);
          }} />
      )}
      {modal?.type === "tx-detail" && (
        <TransactionDetailModal transaction={modal.transaction} customers={customers} accounts={accounts}
          onClose={() => setModal(null)}
          onOpenInvoice={(inv) => goTo(`/detay/fatura/${inv.id}`)} />
      )}
      {modal?.type === "cheque-detail" && (
        <ChequeDetailModal cheque={modal.cheque} customers={customers} onClose={() => setModal(null)} />
      )}
      {modal?.type === "bulk-status" && (
        <BulkStatusModal ids={modal.ids} accounts={accounts}
                         onClose={() => setModal(null)} onSaved={load} />
      )}
      {modal?.type === "settings" && (
        <SettingsModal onClose={() => setModal(null)} onSaved={load} user={user} />
      )}
      {modal?.type === "quick-payment" && (
        <QuickPaymentModal customer={modal.customer} customers={customers} accounts={accounts} direction={modal.direction}
                           onClose={() => setModal(null)} onSaved={load} />
      )}
      {modal?.type === "stock-movements" && (
        <StockMovementsModal
          stockId={modal.stockId}
          onClose={() => setModal(null)}
          onOpenPriceHistory={(sid) => setModal({ type: "price-history", stockId: sid })}
          onOpenInvoice={(iid) => {
            const inv = invoices.find((x) => x.id === iid);
            if (inv) setModal({ type: "invoice-view", invoice: inv });
            else {
              api.get(`/invoices?scope=all`).then((r) => {
                const found = r.data.find((x) => x.id === iid);
                if (found) setModal({ type: "invoice-view", invoice: found });
              });
            }
          }}
        />
      )}
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useMobileTableLabels();
  useEffect(() => {
    // Tema, yazı boyutu ve kalınlık tercihini uygula (localStorage'dan — hesap tercihi giriş sonrası ezer)
    const saved = localStorage.getItem("sp_theme") || "light";
    document.documentElement.setAttribute("data-theme", saved);
    const savedFont = localStorage.getItem("sp_font_size") || "normal";
    document.documentElement.setAttribute("data-font-size", savedFont);
    const savedWeight = localStorage.getItem("sp_font_weight") || "normal";
    document.documentElement.setAttribute("data-font-weight", savedWeight);
    const token = localStorage.getItem("fp_token");
    if (!token) return setChecking(false);
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        // Hesaba kayıtlı görünüm tercihleri varsa cihaz tercihini ez (cihazlar arası kalıcılık)
        const p = r.data?.preferences;
        if (p && (p.theme || p.font_size || p.font_weight)) {
          const t = p.theme || "light";
          const s = p.font_size || "normal";
          const w = p.font_weight || "normal";
          document.documentElement.setAttribute("data-theme", t);
          document.documentElement.setAttribute("data-font-size", s);
          document.documentElement.setAttribute("data-font-weight", w);
          localStorage.setItem("sp_theme", t);
          localStorage.setItem("sp_font_size", s);
          localStorage.setItem("sp_font_weight", w);
        }
      })
      .catch(() => localStorage.removeItem("fp_token"))
      .finally(() => setChecking(false));
  }, []);
  if (checking) return <div className="loading">Serhan Elektronik Muhasebe yükleniyor...</div>;
  if (!user) return <Login onLogin={setUser} />;
  window.__SP_ADMIN = user.role === "admin";
  window.__SP_USER = user;
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("fp_token");
    localStorage.removeItem("sp_panel");
    setUser(null);
  };
  if (window.location.pathname.startsWith("/detay/") || window.location.pathname.startsWith("/is-fisi/")) {
    return (
      <BrowserRouter>
        <DetailPage />
      </BrowserRouter>
    );
  }
  // İş Takip panelinde sadece iş fişi formu (work_only kullanıcı veya 'work' modu seçildi)
  if (user.work_only || localStorage.getItem("sp_panel") === "work" || (user.role !== "admin" && !user.permissions?.access_accounting)) {
    return <WorkPanel user={user} onLogout={logout} />;
  }
  return (
    <BrowserRouter>
      <Shell user={user} onLogout={logout} onUserChange={setUser} />
    </BrowserRouter>
  );
}

export default App;
